/**
 * ViewModeToggle Component
 *
 * Toggle buttons for switching between List, Grid, and Flow view modes.
 *
 * @module features/dashboard/components/ViewModeToggle
 * @see SPEC-016 Requirement 6.3
 */

import React from 'react';
import { cn } from '../../../lib/cn';
import type { ViewMode } from '../store/dashboardStore';

// ============================================================================
// Types
// ============================================================================

export interface ViewModeToggleProps {
  /** Current view mode */
  value: ViewMode;
  /** Change handler */
  onChange: (mode: ViewMode) => void;
  /** Additional class names */
  className?: string;
  /** Disabled state */
  disabled?: boolean;
}

// ============================================================================
// View Mode Definitions
// ============================================================================

interface ViewModeOption {
  id: ViewMode;
  label: string;
  icon: React.ReactNode;
}

const viewModes: ViewModeOption[] = [
  {
    id: 'list',
    label: 'List view',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 6h16M4 10h16M4 14h16M4 18h16"
        />
      </svg>
    ),
  },
  {
    id: 'grid',
    label: 'Grid view',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
        />
      </svg>
    ),
  },
  {
    id: 'flow',
    label: 'Flow view',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
        />
      </svg>
    ),
  },
];

// ============================================================================
// Component
// ============================================================================

/**
 * ViewModeToggle Component
 *
 * Features:
 * - Three view mode options: List, Grid, Flow
 * - Visual indication of selected mode
 * - Keyboard accessible
 * - Smooth transitions
 *
 * @example
 * ```tsx
 * const [viewMode, setViewMode] = useState<ViewMode>('list');
 *
 * <ViewModeToggle
 *   value={viewMode}
 *   onChange={setViewMode}
 * />
 * ```
 *
 * @see Requirement 6.3
 */
export const ViewModeToggle: React.FC<ViewModeToggleProps> = ({
  value,
  onChange,
  className,
  disabled = false,
}) => {
  return (
    <div
      className={cn(
        'inline-flex items-center',
        'border-2 border-linkode-text rounded-linkode',
        'bg-white',
        'overflow-hidden',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      role="radiogroup"
      aria-label="View mode"
    >
      {viewModes.map((mode, index) => (
        <button
          key={mode.id}
          type="button"
          role="radio"
          aria-checked={value === mode.id}
          aria-label={mode.label}
          disabled={disabled}
          onClick={() => onChange(mode.id)}
          className={cn(
            'p-2',
            'transition-all duration-200',
            'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-linkode-teal',
            // Selected state
            value === mode.id
              ? 'bg-linkode-primary text-linkode-text'
              : 'bg-transparent text-linkode-text/60 hover:text-linkode-text hover:bg-linkode-bg-light',
            // Border between buttons
            index > 0 && 'border-l-2 border-linkode-text',
            // Disabled state
            disabled && 'cursor-not-allowed'
          )}
        >
          {mode.icon}
        </button>
      ))}
    </div>
  );
};

// ============================================================================
// Exports
// ============================================================================

export { viewModes };
